import { AppModules } from 'src/models/permissions/RolePermissions';

export enum PromotionComponentType {
  CUSTOM_DATA_TABLE = 'CUSTOM_DATA_TABLE',
  CUSTOM_COA = 'CUSTOM_COA',
  MDA_SHEET = 'MDA_SHEET',
  MDA_WORKSHEET = 'MDA_WORKSHEET',
  TPA_TEMPLATE = 'TPA_TEMPLATE',
  TPA_WORKSHEET = 'TPA_WORKSHEET',
  CLI_TEMPLATE = 'CLI_TEMPLATE',
  CLI_CONFIG = 'CLI_CONFIG',
  PLI_CONFIG = 'PLI_CONFIG',
  BEGINNING_BALANCE = 'BEGINNING_BALANCE',
}

export enum PromotionStatus {
  VALIDATING = 'VALIDATING',
  PROMOTING = 'PROMOTING',
  COMPLETED = 'COMPLETED',
  PENDING_SUBMISSION_REQUIRES_ACTION = 'PENDING_SUBMISSION_REQUIRES_ACTION',
  FAILED = 'FAILED',
  BLOCKED = 'BLOCKED',
}

// Not a promotion status: returned (HTTP 200) when no promotion record exists for the query.
// Treat as "no promotion exists".
export const PROMOTION_NOT_FOUND = 'NOT_FOUND' as const;

export interface PromotionComponent {
  componentType: PromotionComponentType;
  componentId: string;
  componentName?: string;
  navigationPath?: string;
  level: number;
}

export interface ValidateDependenciesRequest {
  componentType: PromotionComponentType;
  componentId: string;
  componentVersion?: number;
}

export interface ValidateDependenciesResponse {
  canPromote: boolean;
  missingDependencies: PromotionComponent[];
  promotionOrder: PromotionComponent[];
}

export interface PromoteComponentRequest {
  componentType: PromotionComponentType;
  componentId: string;
  componentVersion?: number;
  selectedRows?: string[];
}

export interface PromoteComponentResponse {
  promotionId?: string;
  status: PromotionStatus;
  message?: string;
  missingDependencies?: PromotionComponent[];
  lastPromotedAt?: string;
}

export interface GetPromotionStatusResponse {
  // promotionId/componentType/componentId are absent on a NOT_FOUND response.
  promotionId?: string;
  componentType?: PromotionComponentType;
  componentId?: string;
  status: PromotionStatus | typeof PROMOTION_NOT_FOUND;
  message?: string;
  promotedBy?: string;
  promotedAt?: string;
  completedAt?: string;
  recordsPromoted?: number;
}

export interface PromotionRecord {
  promotionId: string;
  componentType: PromotionComponentType;
  componentId: string;
  componentName?: string;
  navigationPath?: string;
  status: PromotionStatus;
  action?: string;
  promotedBy?: string;
  promotedAt?: string;
  completedAt?: string;
  recordsPromoted?: number;
  accountingPeriod?: string;
  selectedRows?: string[];
  errorMessage?: string;
}

export interface GetPromotionHistoryResponse {
  promotions: PromotionRecord[];
  nextToken?: string;
}

export interface PromotionWindowStatusResponse {
  isOpen: boolean;
}

export interface PromotionStageInfo {
  from: string;
  to: string;
}

const STAGE_PROMOTION_MAP: Record<string, PromotionStageInfo> = {
  'alpha': { from: 'Alpha', to: 'Beta' },
  'preprod': { from: 'Sandbox', to: 'Production' },
};

// Website base URL of the environment that promotions from a given stage land in.
// Used by the Promotion History page to build "View in <target>" cross-environment links.
const STAGE_TARGET_WEBSITE_URL: Record<string, string> = {
  'alpha': 'https://beta.tpe.intercompany.fintech.amazon.dev',
  'preprod': 'https://tpe.intercompany.fintech.amazon.dev',
};

/**
 * Maps each promotion component type to the AppModules key used for permission checks.
 * PromoteButton uses this to determine canEdit for the calling user without requiring
 * each consumer to thread a module prop.
 */
export const PROMOTION_COMPONENT_MODULE_MAP: Record<PromotionComponentType, string> = {
  [PromotionComponentType.CUSTOM_DATA_TABLE]: AppModules.CUSTOM_DATA_TABLES,
  [PromotionComponentType.CUSTOM_COA]: AppModules.CUSTOM_COA,
  [PromotionComponentType.MDA_SHEET]: AppModules.MULTIDIMENSIONAL_ALLOCATION,
  [PromotionComponentType.MDA_WORKSHEET]: AppModules.MULTIDIMENSIONAL_ALLOCATION,
  [PromotionComponentType.TPA_TEMPLATE]: AppModules.TP_ALLOCATION,
  [PromotionComponentType.TPA_WORKSHEET]: AppModules.TP_ALLOCATION,
  [PromotionComponentType.CLI_TEMPLATE]: AppModules.CALCULATION_BUILDER,
  [PromotionComponentType.CLI_CONFIG]: AppModules.CALCULATION_BUILDER,
  [PromotionComponentType.PLI_CONFIG]: AppModules.CALCULATION_BUILDER,
  [PromotionComponentType.BEGINNING_BALANCE]: AppModules.PERIOD_CHARGES,
};

/**
 * Component types exempt from the promotion-window gate. These types are always
 * promotable regardless of the window state; only the caller's canEdit is enforced.
 *
 * BB, COA and CDT are usually modified during Month End Close so customers do not
 * want them to be locked during that window. They also do not have any review
 * process in the target environment as the update process is trivial.
 */
export const WINDOW_STATUS_EXEMPT_COMPONENT_TYPES: ReadonlySet<PromotionComponentType> = new Set([
  PromotionComponentType.CUSTOM_DATA_TABLE,
  PromotionComponentType.CUSTOM_COA,
  PromotionComponentType.BEGINNING_BALANCE,
]);

/**
 * Route to the component's detail/edit page for each promotion component type.
 * Returns null for types where the app has no navigable single-page destination
 * (CLI_TEMPLATE / CLI_CONFIG / PLI_CONFIG); the caller renders those as disabled
 * text instead of a link. Callers should base64-encode componentIds for the
 * types listed in COMPONENT_ID_BASE64_ENCODED below.
 */
export const COMPONENT_ID_BASE64_ENCODED: ReadonlySet<PromotionComponentType> = new Set([
  PromotionComponentType.CUSTOM_DATA_TABLE,
  PromotionComponentType.CUSTOM_COA,
  PromotionComponentType.TPA_TEMPLATE
]);

export function getPromotionComponentHref(componentType: PromotionComponentType, componentId: string): string | null {
  const encoded = COMPONENT_ID_BASE64_ENCODED.has(componentType) ? btoa(componentId) : componentId;
  switch (componentType) {
    case PromotionComponentType.CUSTOM_DATA_TABLE:
      return `/calculations/customDataTables/details/${encoded}`;
    case PromotionComponentType.CUSTOM_COA:
      return `/calculations/customCoa/details/${encoded}`;
    case PromotionComponentType.MDA_SHEET:
      return `/calculations/multidimensionalAllocation/sheet/${encoded}`;
    case PromotionComponentType.MDA_WORKSHEET:
      return `/calculations/multidimensionalAllocation/calculation/${encoded}`;
    case PromotionComponentType.TPA_TEMPLATE:
      return `/calculations/tpAllocation/template/${encoded}`;
    default:
      // CLI_TEMPLATE, CLI_CONFIG, PLI_CONFIG, BEGINNING_BALANCE, TPA_WORKSHEET — no dedicated
      // detail page (or not yet wired up). Caller renders these as disabled.
      return null;
  }
}

export const PROMOTION_POLL_INTERVAL_MS = 3000;

// Safety cap so a forgotten/backgrounded tab does not poll indefinitely if the backend never
// reaches a terminal status. 100 attempts × 3s ≈ 5 minutes, comfortably longer than a normal
// promotion but bounded.
export const MAX_PROMOTION_POLL_ATTEMPTS = 100;

export const IN_PROGRESS_STATUSES: string[] = [PromotionStatus.VALIDATING, PromotionStatus.PROMOTING];

export function getPromotionStageInfo(stage: string): PromotionStageInfo | null {
  if (!stage || stage.startsWith('{{') || stage === 'Dev') {
    return { from: 'Sandbox', to: 'Production' };
  }
  return STAGE_PROMOTION_MAP[stage] || null;
}

export function getPromotionTargetWebsiteUrl(stage: string): string | null {
  if (!stage || stage.startsWith('{{') || stage === 'Dev') {
    return STAGE_TARGET_WEBSITE_URL['preprod'];
  }
  return STAGE_TARGET_WEBSITE_URL[stage] || null;
}
